import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Avatar,
  Divider,
  Paper
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Article as ArticleIcon,
  Visibility as ViewIcon,
  Comment as CommentIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { eventsService } from '../services/api';

const EventResults = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [event, setEvent] = useState(null);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (eventId) {
      loadEventResults();
    }
  }, [eventId]);

  const loadEventResults = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 Loading event results...');
      
      // Get published results from the new endpoint
      const response = await eventsService.getEventResults(eventId);
      console.log('🔍 Full API Response:', response);
      
      // Handle the double-nested data structure from makeApiCall
      const apiData = response.data?.data || response.data;
      console.log('🔍 API Data:', apiData);
      
      if (!apiData?.event) {
        console.log('❌ No event data found in response');
        setError('Event not found.');
        setLoading(false);
        return;
      }
      
      setEvent(apiData.event);
      setResults({
        winner: apiData.winner,
        finalists: apiData.finalists || [],
        results_published: apiData.results_published
      });
      
      console.log('✅ Event results loaded successfully');
      
    } catch (err) {
      console.error('❌ Error loading event results:', err);
      if (err.response?.status === 400) {
        setError('Results for this event have not been published yet.');
      } else {
        setError('Failed to load event results. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatWordCount = (count) => {
    return count ? count.toLocaleString() : 'N/A';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/events')}
          sx={{ mb: 2 }}
        >
          Back to Events
        </Button>
        <Alert severity="info">{error}</Alert>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Event not found.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/events')}
          sx={{ mb: 2 }}
        >
          Back to Events
        </Button>
        
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
          🏆 Contest Results
        </Typography>
        
        <Typography variant="h4" color="primary" gutterBottom>
          {event.title}
        </Typography>
        
        {event.description && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {event.description}
          </Typography>
        )}
        
        <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
          {event.theme && (
            <Chip 
              label={`Theme: ${event.theme}`} 
              color="primary" 
              variant="outlined"
            />
          )}
          <Chip 
            icon={<CalendarIcon />}
            label={`${formatDate(event.start_date)} - ${formatDate(event.end_date)}`} 
            variant="outlined"
          />
          <Chip 
            label={`Max ${event.max_word_count} words`} 
            variant="outlined"
          />
          <Chip 
            label="Results Published" 
            color="success"
            icon={<TrophyIcon />}
          />
        </Box>
      </Box>

      {/* Results Section */}
      {results?.winner ? (
        <Grid container spacing={4}>
          {/* Winner Section */}
          <Grid item xs={12}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 4, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Box display="flex" alignItems="center" mb={3}>
                  <TrophyIcon sx={{ fontSize: 48, mr: 2, color: '#FFD700' }} />
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    Winner
                  </Typography>
                </Box>
                
                <Card sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: 'text.primary' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {results.winner.story_title}
                    </Typography>
                    
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6">
                          {results.winner.display_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Winner
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <Chip 
                        icon={<ArticleIcon />}
                        label={`${formatWordCount(results.winner.word_count)} words`}
                        size="small"
                      />
                      <Chip 
                        icon={<ViewIcon />}
                        label={`${results.winner.view_count || 0} views`}
                        size="small"
                      />
                      <Chip 
                        icon={<CommentIcon />}
                        label={`${results.winner.comment_count || 0} comments`}
                        size="small"
                      />
                    </Box>
                    
                    <Button
                      variant="contained"
                      sx={{ mt: 2 }}
                      onClick={() => navigate(`/story/${results.winner.story_id}`)}
                    >
                      Read Winning Story
                    </Button>
                  </CardContent>
                </Card>
              </Box>
              
              {/* Decorative elements */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -50,
                  right: -50,
                  width: 200,
                  height: 200,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  zIndex: 0
                }}
              />
            </Paper>
          </Grid>

          {/* Finalists Section */}
          {results.finalists && results.finalists.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                🥈 Finalists
              </Typography>
              
              <Grid container spacing={3}>
                {results.finalists.map((finalist, index) => (
                  <Grid item xs={12} md={6} lg={4} key={finalist.story_id}>
                    <Card 
                      elevation={2}
                      sx={{ 
                        height: '100%',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4
                        }
                      }}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={2}>
                          <Chip 
                            label={`#${finalist.rank || index + 1}`}
                            color="secondary"
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Finalist
                          </Typography>
                        </Box>
                        
                        <Typography variant="h6" gutterBottom>
                          {finalist.story_title}
                        </Typography>
                        
                        <Box display="flex" alignItems="center" mb={2}>
                          <Avatar sx={{ mr: 2, bgcolor: 'secondary.main', width: 32, height: 32 }}>
                            <PersonIcon fontSize="small" />
                          </Avatar>
                          <Typography variant="body1">
                            {finalist.display_name}
                          </Typography>
                        </Box>
                        
                        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                          <Chip 
                            icon={<ArticleIcon />}
                            label={`${formatWordCount(finalist.word_count)} words`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip 
                            icon={<ViewIcon />}
                            label={`${finalist.view_count || 0} views`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip 
                            icon={<CommentIcon />}
                            label={`${finalist.comment_count || 0} comments`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => navigate(`/story/${finalist.story_id}`)}
                        >
                          Read Story
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      ) : (
        <Alert severity="info">
          Results for this event are not available yet.
        </Alert>
      )}

      {/* Event Stats */}
      <Box mt={6}>
        <Divider sx={{ mb: 3 }} />
        <Typography variant="h5" gutterBottom>
          Contest Statistics
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main">
                {event.participant_count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Participants
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="secondary.main">
                {event.total_submissions || event.submission_count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Submissions
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {event.max_word_count}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Max Words
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">
                {Math.ceil((new Date(event.end_date) - new Date(event.start_date)) / (1000 * 60 * 60 * 24))}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contest Days
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default EventResults;
